/**
 * ModalShell — reusable modal container with backdrop, keyboard, and dismiss.
 *
 * Provides the full modal chrome shared by every dialog in PoseRef:
 * - Semi-transparent backdrop (MODAL_BACKDROP_STYLE)
 * - Centered glassmorphic shell (MODAL_SHELL_STYLE)
 * - Escape → onClose, Enter → onConfirm (optional)
 * - Click-outside dismiss
 * - Non-modifier key blocking (prevents G/R/S from leaking to shortcuts)
 *
 * Also accepts a `portal` prop for rendering via React portal (used by
 * ReferenceLibraryModal and similar overlay-heavy dialogs).
 *
 * Both the backdrop and the shell carry a `data-floating-portal` attribute
 * so that outside-click dismissal in any parent surface (floating panels,
 * popovers, sidebar temporary panels) treats clicks inside the modal as
 * "inside" — a modal opened from within a panel does not close the panel.
 */

import type { ComponentType, CSSProperties, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button.tsx";
import { useModalKeyboard, useOutsideClick } from "./shared.tsx";
import { MODAL_BACKDROP_STYLE, MODAL_SHELL_STYLE } from "./styles.ts";
import {
  COLOR_TEXT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_MODAL_TITLE,
} from "./tokens.ts";

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** Called when Enter is pressed. Omit to disable Enter handling. */
  onConfirm?: () => void;
  /** Shell width in pixels. */
  width?: number;
  /** Extra styles merged onto the shell div (e.g. maxHeight, gap overrides). */
  style?: CSSProperties;
  /** Render inside a React portal to document.body. */
  portal?: boolean;
  /** Auto-focus this ref on mount (e.g. a confirm button). */
  autoFocusRef?: React.RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function ModalShell({
  open,
  onClose,
  onConfirm,
  width,
  style,
  portal,
  autoFocusRef,
  children,
}: ModalShellProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Keyboard: Escape → close, Enter → confirm
  useModalKeyboard(open, { onEscape: onClose, onEnter: onConfirm });

  // Click outside → close
  useOutsideClick(panelRef, onClose, open);

  // Auto-focus on mount
  useEffect(() => {
    if (open && autoFocusRef?.current) {
      requestAnimationFrame(() => autoFocusRef.current?.focus());
    }
  }, [open, autoFocusRef]);

  if (!open) return null;

  const content = (
    <>
      <div data-floating-portal style={MODAL_BACKDROP_STYLE} />
      <div
        ref={panelRef}
        data-floating-portal
        style={{
          ...MODAL_SHELL_STYLE,
          ...(width != null ? { width } : undefined),
          ...style,
        }}
      >
        {children}
      </div>
    </>
  );

  return portal ? createPortal(content, document.body) : content;
}

// ---------------------------------------------------------------------------
// ModalHeader — icon + title row
// ---------------------------------------------------------------------------

/**
 * Any lucide-react icon is an `FC<{ size, color, style, ... }>`.
 * We type it loosely so callers can pass any icon component.
 */
type IconComponent = ComponentType<{
  size?: number | string;
  color?: string;
  style?: CSSProperties;
}>;

interface ModalHeaderProps {
  /** Lucide-react icon component (or any icon that accepts size/style). */
  icon: IconComponent;
  /** Color applied to the icon — defaults to the icon's inherited color. */
  iconColor?: string;
  /** Header title text. */
  title: ReactNode;
}

/**
 * Standard modal header: a 18px icon + bold title, flex-aligned with a
 * 10px gap. Drop into any `ModalShell` body.
 */
export function ModalHeader({
  icon: Icon,
  iconColor,
  title,
}: ModalHeaderProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Icon size={18} style={{ color: iconColor, flexShrink: 0 }} />
      <span
        style={{
          fontSize: FONT_SIZE_MODAL_TITLE,
          fontWeight: 600,
          color: COLOR_TEXT,
        }}
      >
        {title}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModalActions — right-aligned button row
// ---------------------------------------------------------------------------

interface ModalActionsProps {
  /** Buttons (or any action elements) — rendered in order. */
  children: ReactNode;
  /** Extra top margin, matching the existing `marginTop: 4` override. */
  marginTop?: number;
}

/**
 * Standard modal footer: a right-aligned row of action buttons, 8px gap.
 * Order actions secondary-first, primary-last.
 */
export function ModalActions({ children, marginTop }: ModalActionsProps) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginTop,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageDialog — icon header + body text + configurable action buttons
// ---------------------------------------------------------------------------

export interface MessageDialogAction {
  /** Button label. */
  label: string;
  /** Visual variant; defaults to "secondary". */
  kind?: "primary" | "secondary";
  /** Override the button's text color (e.g. for a caution secondary). */
  color?: string;
  onClick: () => void;
  /** Auto-focus on mount — wire one action as the Enter target. */
  autoFocus?: boolean;
}

interface MessageDialogProps {
  icon: IconComponent;
  iconColor?: string;
  title: ReactNode;
  message: ReactNode;
  actions: MessageDialogAction[];
  /** Pressed on Escape. */
  onClose: () => void;
  /** Pressed on Enter. If omitted, the autoFocus action (if any) is used. */
  onConfirm?: () => void;
  width?: number;
}

/**
 * Composes ModalShell + ModalHeader + body text + ModalActions for the common
 * "icon + title + message + buttons" dialog shape. Each confirm/apply/save
 * dialog in ConfirmDialog.tsx is a one-line declaration using this primitive.
 */
export function MessageDialog({
  icon,
  iconColor,
  title,
  message,
  actions,
  onClose,
  onConfirm,
  width,
}: MessageDialogProps) {
  const autoFocusRef = useRef<HTMLButtonElement>(null);
  const autoFocusAction = actions.find((a) => a.autoFocus);
  const resolvedOnConfirm = onConfirm ?? autoFocusAction?.onClick;

  return (
    <ModalShell
      open
      onClose={onClose}
      onConfirm={resolvedOnConfirm}
      width={width}
      autoFocusRef={autoFocusAction ? autoFocusRef : undefined}
    >
      <ModalHeader icon={icon} iconColor={iconColor} title={title} />
      <div
        style={{
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_SECONDARY,
          lineHeight: 1.5,
        }}
      >
        {message}
      </div>
      <ModalActions>
        {actions.map((action, i) => {
          const variant = action.kind === "primary" ? "primary" : "secondary";
          return (
            <Button
              key={i}
              ref={action === autoFocusAction ? autoFocusRef : undefined}
              onClick={action.onClick}
              variant={variant}
              style={action.color ? { color: action.color } : undefined}
            >
              {action.label}
            </Button>
          );
        })}
      </ModalActions>
    </ModalShell>
  );
}
